import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import { AddCircleLinearIcon } from '../../../../../../../icons-lib';
import i18n from '../../../../../../../i18';

export const ImageUploadField = ({ field, data, onChange, context }) => {
    const { id, label, helpText, needDivider } = field;
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);
    
    // Validation constants
    const MAX_FILE_SIZE = 1024 * 1024; // 1 MB in bytes
    const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
    
    const validateFile = (file) => {
        const errors = [];
        
        // File type check
        if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
            errors.push(i18n.t('errors.imageInvalidFormat'));
        }
        
        // File size check
        if (file.size > MAX_FILE_SIZE) {
            const maxSizeMB = MAX_FILE_SIZE / (1024 * 1024);
            errors.push(i18n.t('errors.imageTooBig', { maxSize: maxSizeMB }));
        }
        
        return errors;
    };
    
    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };
    
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        // File validation
        const validationErrors = validateFile(file);
        if (validationErrors.length > 0) {
            setError(validationErrors[0]);
            // Clear input to allow re-selection
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }
        
        setIsUploading(true);
        setError('');
        
        try {
            // Use new endpoint for campaign image upload
            const formData = new FormData();
            formData.append('file', file);
            formData.append('isPublic', 'true');

            const response = await context.getState().server.marketingCampaigns.uploadImage(formData);
            
            if (response.error !== 'OK') {
                throw new Error(response.error);
            }

            // Create URL for preview
            const imageURL = URL.createObjectURL(file);
            
            const imageData = {
                id: response.data.id,
                name: response.data.name,
                url: imageURL,
                imageUrl: response.data.name,
                type: response.data.type,
                size: response.data.size
            };
            
            onChange(id, imageData);
        } catch (uploadError) {
            setError(i18n.t('errors.imageUploadError') + ': ' + uploadError.message);
        } finally {
            setIsUploading(false);
        }
    };
    
    const handleRemove = () => {
        onChange(id, null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    
    const translatedLabel = i18n.t(`columns.${label}`);
    const translatedHelpText = helpText ? i18n.t(`helpText.${helpText}`) : '';
    
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                {translatedLabel}
            </Typography>
            
            {!data && (
                <Card 
                    sx={{ 
                        'border': '2px dashed #e0e0e0',
                        'backgroundColor': '#fafafa',
                        'cursor': 'pointer',
                        '&:hover': {
                            'backgroundColor': '#f5f5f5',
                            'borderColor': '#bdbdbd'
                        }
                    }}
                    onClick={handleFileSelect}
                >
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <AddCircleLinearIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                            {i18n.t('buttons.clickToUploadImage')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {i18n.t('helpText.imageUploadFormats')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {i18n.t('helpText.imageUploadMaxSize', { maxSize: '1 MB' })}
                        </Typography>
                    </CardContent>
                </Card>
            )}
            
            {data && (
                <Card sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: 1,
                                overflow: 'hidden',
                                flexShrink: 0
                            }}
                        >
                            <img
                                src={data.url}
                                alt={data.name}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        </Box>
                        
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" color="text.secondary">
                                {i18n.t('labels.fileName')}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {data.name}
                            </Typography>
                            {data.size && (
                                <Typography variant="body2" color="text.secondary">
                                    {formatFileSize(data.size)}
                                </Typography>
                            )}
                        </Box>
                        
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleRemove}
                            sx={{ minWidth: 'auto', px: 2 }}
                        >
                            {i18n.t('buttons.remove')}
                        </Button>
                    </Box>
                </Card>
            )}
            
            {error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}
            
            {translatedHelpText && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {translatedHelpText}
                </Typography>
            )}
            
            <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                disabled={isUploading}
            />
            
            {needDivider && <Divider sx={{ mt: 2 }} />}
        </Box>
    );
}; 